import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Copy,
  Info,
  Loader2,
  ShieldAlert,
  X,
} from "lucide-react";

import { supabase, getCurrentSession } from "../lib/supabase";
import {
  buildInstallCommand,
  extractFunctionErrorMessage,
} from "../lib/agentInstall";
import { ProjectRulesNudge } from "./ProjectRulesNudge";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// ============================================================================
// ConnectAgentModal
//
// Claude Code connection dialog used by TeamAgents.tsx. Reuses the
// mint-install-token / install.sh / claim-install-token pipeline from
// AgentInstallManager.tsx. Terminal installation is the supported path.
//
// "Human owner" maps directly to agents.user_id (already a required column,
// separate from created_by) via mint-install-token's new optional
// owner_user_id param -- see that function's 2026-09-09 comment. Defaults to
// whoever opened this dialog.
//
// 2026-10-08 bugfix: this used to be a plain `fixed inset-0` div, not a real
// Radix Dialog. That was fine standalone (WelcomeModal, its only caller
// until today, is itself a plain full-screen div with no competing Radix
// layer), but broke when TeamAgents.tsx started opening this from inside a
// shadcn Sheet (a Radix Dialog): Radix's modal Dialog calls `hideOthers()`
// (via the `aria-hidden` package) on every DOM node outside its own Content
// element the moment it opens, marking them inert -- and this component's
// div lived outside the Sheet's Content, so once the Sheet stopped
// auto-closing on clicks here (the previous fix), Radix's own
// Sheet-is-modal behavior made this entire dialog inert instead, matching
// the "nothing is clickable" report. Rebuilding this on the real
// DialogPrimitive makes it a proper nested Radix Dialog: each modal
// Dialog's hideOthers() call only ever targets whatever's rendered at that
// moment, so a second (inner) Dialog's own Content is never the thing it
// hides, regardless of where in the tree it's mounted -- nested Radix
// Dialogs are a supported pattern, unlike a hand-rolled div faking one.
// Backdrop-click-to-dismiss is deliberately suppressed below (matches this
// component's original behavior, which had no backdrop click handler at
// all) so a stray click mid-flow can't silently drop progress; Escape and
// the X/Cancel buttons still close it.
// ============================================================================

interface TeamMemberOption {
  user_id: string;
  label: string;
}

interface ProjectOption {
  id: string;
  name: string;
}

interface ConnectAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  /** Called once an agent row is actually detected for this client_id. */
  onConnected: () => void;
}

const POLL_INTERVAL_MS = 4000;

export const ConnectAgentModal: React.FC<ConnectAgentModalProps> = ({
  isOpen,
  onClose,
  teamId,
  onConnected,
}) => {
  const [members, setMembers] = useState<TeamMemberOption[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [ownerUserId, setOwnerUserId] = useState<string>("");
  const [projectId, setProjectId] = useState<string>("none");
  const [displayName, setDisplayName] = useState("");

  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);
  const [claimCode, setClaimCode] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [connected, setConnected] = useState(false);

  // Load the team roster (for "Human owner") and projects (for the optional
  // project field) once the dialog opens. Reuses get-all-team-users, the
  // same edge function TeamManagement.tsx already relies on for its member
  // list -- no new backend surface needed for this part.
  useEffect(() => {
    if (!isOpen) return;

    setMintError(null);
    setClaimCode(null);
    setClientId(null);
    setConnected(false);
    setDisplayName("");
    setProjectId("none");

    const load = async () => {
      const session = await getCurrentSession();
      if (!session) return;

      const { data: authData } = await supabase.auth.getUser();
      const authUserId = authData?.user?.id ?? null;

      const [membersRes, projectsRes] = await Promise.all([
        supabase.functions.invoke("get-all-team-users", {
          body: { team_id: teamId },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        (supabase.from as any)("projects")
          .select("id, name")
          .eq("team_id", teamId)
          .is("deleted_at", null)
          .order("name", { ascending: true }),
      ]);

      const rows = (membersRes.data?.users ?? []) as Array<{
        user_id: string;
        auth_user_id: string | null;
        user_name: string | null;
        user_email: string | null;
      }>;
      const options: TeamMemberOption[] = rows.map((r) => ({
        user_id: r.user_id,
        label: r.user_name || r.user_email || "Unnamed teammate",
      }));
      setMembers(options);

      const allProjects = (projectsRes.data ?? []) as ProjectOption[];

      const self = rows.find((r) => r.auth_user_id === authUserId);
      if (self) {
        setCurrentUserId(self.user_id);
        setOwnerUserId(self.user_id);
      }

      // Scope the dropdown to the CALLER's own pod, same rule
      // AgentInstallManager.tsx already enforces (2026-09-09 decision: a
      // member should only be able to pick a project their own pod has been
      // assigned, not every project on the team) -- no pod, or no projects
      // assigned to that pod, both resolve to an empty list rather than
      // falling back to showing everything.
      let podScopedProjects: ProjectOption[] = [];
      if (authUserId) {
        const { data: userRow } = await (supabase.from as any)("users")
          .select("pod_id")
          .eq("auth_user_id", authUserId)
          .eq("team_id", teamId)
          .maybeSingle();

        const podId = (userRow?.pod_id as string | null | undefined) ?? null;
        if (podId) {
          const { data: podProjectRows } = await (supabase.from as any)(
            "project_pods",
          )
            .select("project_id")
            .eq("pod_id", podId);
          const allowedProjectIds = new Set(
            (podProjectRows ?? []).map(
              (row: { project_id: string }) => row.project_id,
            ),
          );
          podScopedProjects = allProjects.filter((p) =>
            allowedProjectIds.has(p.id),
          );
        }
      }
      setProjects(podScopedProjects);
    };

    load().catch((err) =>
      console.error("Error loading connect-agent options:", err),
    );
  }, [isOpen, teamId]);

  const activeCommand = claimCode ? buildInstallCommand(claimCode) : "";

  const handleGenerate = useCallback(async () => {
    setMinting(true);
    setMintError(null);

    const session = await getCurrentSession();
    if (!session) {
      setMintError(
        "Your session has expired. Please refresh the page and sign in again.",
      );
      setMinting(false);
      return;
    }

    const { data, error } = await supabase.functions.invoke(
      "mint-install-token",
      {
        body: {
          team_id: teamId,
          agent_name: displayName.trim() || undefined,
          project_id: projectId !== "none" ? projectId : undefined,
          owner_user_id:
            ownerUserId && ownerUserId !== currentUserId
              ? ownerUserId
              : undefined,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      },
    );

    if (error) {
      setMintError(await extractFunctionErrorMessage(error));
      setMinting(false);
      return;
    }

    const newClaimCode = data?.data?.claim_code as string | undefined;
    const newClientId = data?.data?.client_id as string | undefined;
    if (!newClaimCode || !newClientId) {
      setMintError(
        "The server did not return an install command. Please try again.",
      );
      setMinting(false);
      return;
    }

    setClaimCode(newClaimCode);
    setClientId(newClientId);
    setMinting(false);
  }, [displayName, projectId, ownerUserId, currentUserId, teamId]);

  // 2026-09-17: "connected" now means the install actually completed
  // (hook_install_tokens.claimed_at, set by claim-install-token the moment
  // install.sh finishes) -- not that a real check-in already happened. This
  // used to only check `agents` (created by agent-auto-checkin on the first
  // real Stop-hook fire), same signal AgentInstallManager used to move an
  // install from "pending" to "active" -- both now check claimed_at first,
  // so this dialog, the onboarding wizard's outer tool list, and the Agents
  // page all agree on what "connected" means. Still also checks `agents`,
  // both because a real check-in obviously still counts, and as a fallback
  // for any future agent-creation path that doesn't go through the
  // claim-token flow.
  useEffect(() => {
    if (!isOpen || !clientId || connected) return;

    let cancelled = false;
    const check = async () => {
      const [agentRes, tokenRes] = await Promise.all([
        (supabase.from as any)("agents")
          .select("id")
          .eq("team_id", teamId)
          .eq("client_id", clientId)
          .is("deleted_at", null)
          .maybeSingle(),
        (supabase.from as any)("hook_install_tokens")
          .select("id")
          .eq("client_id", clientId)
          .not("claimed_at", "is", null)
          .is("revoked_at", null)
          .maybeSingle(),
      ]);
      if (!cancelled && (agentRes.data || tokenRes.data)) {
        setConnected(true);
        onConnected();
      }
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOpen, clientId, connected, teamId, onConnected]);

  const handleCopy = async () => {
    if (!activeCommand) return;
    await navigator.clipboard.writeText(activeCommand);
    setCopyState("copied");
    setTimeout(() => setCopyState("idle"), 2000);
  };

  const ownerOptions = useMemo(() => {
    if (currentUserId && !members.some((m) => m.user_id === currentUserId)) {
      return [{ user_id: currentUserId, label: "You" }, ...members];
    }
    return members.map((m) =>
      m.user_id === currentUserId ? { ...m, label: `${m.label} (you)` } : m,
    );
  }, [members, currentUserId]);

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-ink-900/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-[80] flex max-h-[90vh] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-card-foreground shadow-2xl"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Description className="sr-only">
            Connect a Claude Code agent to this team.
          </DialogPrimitive.Description>
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
            <div className="flex items-start gap-3">
              <img
                src="/agent-tools/claude.svg"
                alt=""
                className="mt-0.5 h-10 w-10 shrink-0 rounded-xl"
              />
              <div>
                <DialogPrimitive.Title className="text-xl font-semibold tracking-tight text-foreground">
                  Connect Claude Code
                </DialogPrimitive.Title>
                <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Terminal install &middot; auto check-in
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 rounded-full bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {/* Section 1 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                  1
                </span>
                <h3 className="text-sm font-semibold text-foreground">
                  Connect using your terminal
                </h3>
              </div>
              <Info
                className="h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
            </div>

            <p className="text-sm text-muted-foreground">
              Generate an install command, then copy and run it in your terminal.
              Restart Claude Code to finish connecting. Use this setup on another computer to add a separate connector. Running it again on this computer replaces its active connection.
            </p>

            {mintError && (
              <Alert variant="destructive">
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription>{mintError}</AlertDescription>
              </Alert>
            )}

            {claimCode ? (
              // No border-radius, per Figma (2026-09-29) -- was rounded-xl.
              <div className="space-y-3 border border-border bg-muted/40 p-4">
                <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">
                  <li>
                    Copy the command below and run it in your own terminal.
                  </li>
                  <li>
                    Restart Claude Code (<code className="text-xs">/exit</code>{" "}
                    then <code className="text-xs">claude</code>) so the hook
                    loads.
                  </li>
                </ol>
                <Textarea
                  value={activeCommand}
                  readOnly
                  rows={2}
                  className="font-mono text-xs leading-5"
                  aria-label="Claude Code install command"
                />
                <div className="flex items-center justify-between gap-2">
                  <Button size="sm" onClick={handleCopy}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" />
                    {copyState === "copied" ? "Copied!" : "Copy command"}
                  </Button>
                  {connected ? (
                    <Badge variant="success" className="gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-success" />
                      Connected to Claude Code
                    </Badge>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Waiting for install to complete…
                    </span>
                  )}
                </div>
                {connected && <ProjectRulesNudge teamId={teamId} />}
              </div>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={minting}
                className="w-full"
              >
                {minting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  "Generate install command"
                )}
              </Button>
            )}
          </div>

          {/* Section 2 */}
          {/* No border-radius, per Figma (2026-09-29) -- was rounded-xl. */}
          <div className="space-y-3 border border-border bg-muted/40 p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                2
              </span>
              <h3 className="text-sm font-semibold text-foreground">
                Identity &amp; ownership
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Display name
                </label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Ben's connector"
                  maxLength={60}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Human owner
                </label>
                <Select value={ownerUserId} onValueChange={setOwnerUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="z-[90]">
                    {ownerOptions.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Project (optional)
              </label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="No project" />
                </SelectTrigger>
                <SelectContent className="z-[90]">
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {projects.length === 0
                  ? 'No projects are assigned to your team yet, ask an admin to assign one from the Projects page, or leave this as "No project" for now.'
                  : "Only a fallback for usage a rule doesn't already catch. If this agent works across more than one project, a repo-based rule (set up by an admin) splits its usage automatically instead. Change this default any time from the agent's detail page."}
              </p>
            </div> */}
          </div>
        </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            <Button variant="outline" onClick={onClose}>
              {connected ? "Done" : "Cancel"}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default ConnectAgentModal;
